import { ReactNode } from 'react';
import {
	Anchor,
	Badge,
	Image,
	Burger,
	Button,
	Center,
	Divider,
	Group,
	MantineProvider as Mantine,
	MantineThemeOverride,
	Menu,
	Modal,
	NativeSelect,
	Pagination,
	Radio,
	RadioGroup,
	Select,
	Tooltip,
	Notification,
	Stack,
	Checkbox,
	Popover,
	Accordion,
	BackgroundImage,
	Title,
	Drawer,
	Highlight,
	RangeSlider,
	Slider,
	Progress,
	Overlay,
	LoadingOverlay,
	PasswordInput,
	Textarea,
	TextInput,
	Loader as MantineLoader,
	NumberInput,
	SegmentedControl,
	Chip,
	Table,
	Flex,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { Loader } from '@/components/ui/loading/partials/Loader';
import ArrowDownIcon from '@/assets/icons/arrowDown.svg?react';
import EyeIcon from '@/assets/icons/eye.svg?react';
import CloseIcon from '@/assets/icons/close.svg?react';
import InfoIcon from '@/assets/icons/info.svg?react';
import CheckIcon from '@/assets/icons/check.svg?react';
import APP_CONFIG from '@/configs/appConfig';
import classNames from 'classnames';
import styles from '../sass/mantine.module.scss';
import '@mantine/core/styles.layer.css';
import '@mantine/dates/styles.layer.css';

const colors: MantineThemeOverride['colors'] = {
	cream: [
		'#fdfcf9',
		'#f7f5f1',
		'#f4f1eb',
		'#e8e4dd',
		'#D5CABD',
		'#BEB3A8',
		'#A69D93',
		'#8E867E',
		'#777069',
		'#5F5A54',
	],
};

const components: MantineThemeOverride['components'] = {
	Chip: Chip.extend({
		defaultProps: {
			radius: 'sm',
			size: 'md',
		},
		classNames: {
			label: styles.chipLabel,
		},
	}),
	Pagination: Pagination.extend({
		defaultProps: {
			withControls: true,
			gap: 'sm',
			radius: 3,
		},
		classNames: {
			control: styles.paginationControl,
			dots: styles.paginationDots,
		},
	}),
	Burger: Burger.extend({
		defaultProps: {
			color: 'green.9',
			size: 30,
		},
	}),
	Badge: Badge.extend({
		defaultProps: {
			radius: 'sm',
			px: 'sm',
			py: 'xs',
			color: 'dark.6',
		},
		classNames: {
			label: styles.badgeLabel,
		},
	}),
	Modal: Modal.extend({
		defaultProps: {
			centered: true,
			withCloseButton: true,
			radius: 'sm',
			zIndex: APP_CONFIG.Z_INDEX.MODAL,
			closeButtonProps: {
				size: 24,
				icon: <CloseIcon />,
			},
			size: APP_CONFIG.BREAKPOINTS.XL,
			closeOnClickOutside: false,
		},
		classNames: {
			root: styles.modalRoot,
			inner: styles.modalInner,
			content: styles.modalContent,
			header: styles.modalHeader,
			body: styles.modalBody,
			close: styles.modalClose,
			title: styles.modalTitle,
		},
	}),
	SegmentedControl: SegmentedControl.extend({
		defaultProps: {
			color: 'black',
			size: 'md',
		},
		classNames: {
			root: styles.segmentedControlRoot,
			label: styles.segmentedControlLabel,
		},
	}),
	Button: Button.extend({
		defaultProps: {
			px: 'sm',
			radius: 'sm',
		},
	}),
	Menu: Menu.extend({
		defaultProps: {
			shadow: 'md',
			radius: 'sm',
		},
		classNames: {},
	}),
	Divider: Divider.extend({
		defaultProps: {
			labelPosition: 'left',
			size: 'xs',
		},
		classNames: {
			label: styles.dividerLabel,
		},
	}),
	Select: Select.extend({
		defaultProps: {
			radius: 'sm',
			size: 'md',
			withCheckIcon: false,
			allowDeselect: false,
			comboboxProps: {
				transitionProps: {
					duration: 100,
					transition: 'scale-y',
					timingFunction: 'ease',
				},
				zIndex: APP_CONFIG.Z_INDEX.SELECT,
			},
		},
		classNames: {
			input: styles.selectInput,
			option: styles.selectOption,
			dropdown: styles.selectDropdown,
			empty: styles.selectEmpty,
		},
	}),
	Image: Image.extend({
		defaultProps: {
			radius: 'sm',
			fit: 'cover',
		},
		classNames: {
			root: 'fade-in',
		},
	}),
	NativeSelect: NativeSelect.extend({
		defaultProps: {
			rightSection: <ArrowDownIcon />,
			radius: 'sm',
			size: 'lg',
		},
		classNames: {
			input: styles.selectInput,
			section: styles.selectRightSection,
		},
	}),
	Tooltip: Tooltip.extend({
		defaultProps: {
			position: 'top',
			transitionProps: {
				transition: 'pop',
				duration: 200,
			},
			multiline: true,
			withArrow: true,
			arrowSize: 6,
			events: { hover: true, focus: true, touch: true },
			color: 'black',
			opacity: 0.9,
			radius: 3,
			zIndex: APP_CONFIG.Z_INDEX.TOOLTIP,
		},
		classNames: {
			tooltip: styles.tooltipTooltip,
		},
	}),
	Group: Group.extend({
		classNames: {
			root: styles.groupRoot,
		},
	}),
	RadioGroup: RadioGroup.extend({}),
	Radio: Radio.extend({
		defaultProps: {
			size: 'xs',
			color: '#f9871b',
		},
		classNames: {
			root: styles.radioRoot,
			body: styles.radioBody,
			labelWrapper: styles.radioLabelWrapper,
			label: styles.radioLabel,
		},
	}),
	Anchor: Anchor.extend({
		defaultProps: {
			underline: 'always',
			c: 'black',
			fz: 16,
			fw: 400,
		},
	}),
	Stack: Stack.extend({
		classNames: {
			root: styles.stackRoot,
		},
	}),
	Checkbox: Checkbox.extend({
		defaultProps: {
			size: 'md',
			radius: 'sm',
		},
	}),
	TextInput: TextInput.extend({
		defaultProps: {
			radius: 'sm',
			size: 'md',
		},
		classNames: {
			input: styles.textInputInput,
			label: styles.textInputLabel,
			error: styles.textInputError,
		},
	}),
	NumberInput: NumberInput.extend({
		defaultProps: {
			radius: 'sm',
			size: 'md',
			hideControls: true,
		},
		classNames: {
			input: styles.textInputInput,
			label: styles.textInputLabel,
			error: styles.textInputError,
		},
	}),
	Textarea: Textarea.extend({
		defaultProps: {
			size: 'lg',
			radius: 'sm',
			autosize: true,
			minRows: 4,
			maxRows: 8,
		},
		classNames: {
			input: styles.textareaInput,
		},
	}),
	PasswordInput: PasswordInput.extend({
		defaultProps: {
			radius: 3,
			visibilityToggleIcon: ({ reveal }) => (
				<Center
					className={classNames({
						[styles.passwordInputIconRevealed]: reveal,
					})}
				>
					<EyeIcon />
				</Center>
			),
		},
		classNames: {
			wrapper: styles.passwordInputWrapper,
			input: styles.passwordInputInput,
			section: styles.passwordInputSection,
			error: styles.passwordInputError,
		},
	}),
	Loader: MantineLoader.extend({
		defaultProps: {
			loaders: { ...MantineLoader.defaultLoaders, spinner: Loader },
			type: 'spinner',
		},
	}),
	LoadingOverlay: LoadingOverlay.extend({
		defaultProps: {
			loaderProps: {
				type: 'spinner',
			},
			overlayProps: {
				opacity: 0.5,
			},
			zIndex: APP_CONFIG.Z_INDEX.OVERLAY,
		},
	}),
	Overlay: Overlay.extend({
		defaultProps: {
			zIndex: APP_CONFIG.Z_INDEX.OVERLAY,
			backgroundOpacity: 0.25,
		},
	}),
	Progress: Progress.extend({
		defaultProps: {
			size: 'md',
			color: 'green.6',
			radius: 'md',
		},
		styles: ({ colors }) => ({
			root: {
				backgroundColor: colors.green[0],
			},
		}),
	}),
	Slider: Slider.extend({
		defaultProps: {
			labelTransitionProps: {
				transition: 'fade',
				duration: 250,
			},
			thumbSize: 22,
			size: 6,
		},
		classNames: {
			root: styles.sliderRoot,
			bar: styles.sliderBar,
			thumb: styles.sliderThumb,
			track: styles.sliderTrack,
			label: styles.sliderLabel,
			markLabel: styles.sliderMarkLabel,
		},
	}),
	RangeSlider: RangeSlider.extend({
		defaultProps: {
			color: 'dark.5',
			labelTransitionProps: {
				transition: 'fade',
				duration: 250,
			},
			minRange: 1,
		},
	}),
	Highlight: Highlight.extend({
		defaultProps: {
			color: 'dark',
			highlightStyles: {
				fontWeight: 700,
			},
		},
	}),
	DatePicker: DatePicker.extend({
		classNames: {
			day: styles.datePickerDay,
		},
	}),
	Table: Table.extend({
		defaultProps: {
			striped: true,
			withTableBorder: true,
		},
		classNames: {},
	}),
	Drawer: Drawer.extend({
		defaultProps: {
			position: 'left',
			zIndex: APP_CONFIG.Z_INDEX.DRAWER,
			size: 500,
			withCloseButton: false,
			overlayProps: {
				opacity: 0,
				display: 'none',
			},
		},
		classNames: {
			overlay: styles.drawerInner,
			content: styles.drawerContent,
			inner: styles.drawerInner,
			body: styles.drawerBody,
			header: styles.drawerHeader,
			title: styles.drawerTitle,
			close: styles.drawerClose,
		},
	}),
	Title: Title.extend({
		defaultProps: {
			lts: -0.25,
		},
	}),
	Notification: Notification.extend({
		defaultProps: { radius: 3, withCloseButton: false },
		styles: {
			root: {
				zIndex: APP_CONFIG.Z_INDEX.NOTIFICATION,
			},
		},
		classNames: {
			root: styles.notificationRoot,
			icon: styles.notificationIcon,
			closeButton: styles.notificationCloseButton,
		},
	}),
	BackgroundImage: BackgroundImage.extend({
		defaultProps: {},
	}),
	Flex: Flex.extend({
		defaultProps: {
			align: 'center',
		},
	}),
	Accordion: Accordion.extend({
		defaultProps: {
			chevronPosition: 'right',
			chevronSize: 20,
			transitionDuration: 250,
		},
		classNames: {
			content: styles.accordionContent,
			item: styles.accordionItem,
			control: styles.accordionControl,
			label: styles.accordionLabel,
			chevron: styles.accordionChevron,
			panel: styles.accordionPanel,
		},
	}),
	Popover: Popover.extend({
		defaultProps: {
			radius: 'sm',
			zIndex: APP_CONFIG.Z_INDEX.POPOVER,
		},
		classNames: {
			dropdown: styles.popoverDropdown,
		},
	}),
};

const headings: MantineThemeOverride['headings'] = {
	fontFamily: 'Quicksand',
	fontWeight: '600',
};

const other: MantineThemeOverride['other'] = {
	notificationProps: {
		info: {
			classNames: {
				root: styles.notificationInfo,
				icon: styles.notificationInfoIcon,
			},
			icon: <InfoIcon />,
		},
		danger: {
			classNames: {
				root: styles.notificationDanger,
				icon: styles.notificationDangerIcon,
			},
			icon: <CloseIcon />,
		},
		warning: {},
		success: {
			classNames: {
				root: styles.notificationSuccess,
				icon: styles.notificationSuccessIcon,
			},
			icon: <CheckIcon />,
		},
	},
};

const breakpoints = Object.entries(APP_CONFIG.BREAKPOINTS).reduce<
	MantineThemeOverride['breakpoints']
>((acc, curr) => {
	acc![curr[0].toLowerCase()] = curr[1];
	return acc;
}, {});

export const MantineProvider = ({ children }: { children: ReactNode }) => {
	return (
		<Mantine
			theme={{
				fontFamily: 'Sen',
				white: '#ffffff',
				black: '#141414',
				colors,
				other,
				cursorType: 'pointer',
				components,
				headings,
				breakpoints,
				primaryColor: 'dark',
				primaryShade: 9,
			}}
		>
			{children}
		</Mantine>
	);
};
